import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { SignInWithApple } from '@capacitor-community/apple-sign-in';
import { BarcodeScanner } from '@capacitor-community/barcode-scanner';
import { FacebookLogin } from '@capacitor-community/facebook-login';
import { Media } from '@capacitor-community/media';
import { Browser } from '@capacitor/browser';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Device } from '@capacitor/device';
import { Geolocation, Position } from '@capacitor/geolocation';
import { SplashScreen } from '@capacitor/splash-screen';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';
import { Screenshot } from '@ionic-native/screenshot/ngx';
import { LoadingController, Platform, ToastController } from '@ionic/angular';
import { StartNavigation } from '@proteansoftware/capacitor-start-navigation';
import { DateTime } from 'luxon';
import { BehaviorSubject, Observable, from, of } from 'rxjs';
import {
  catchError,
  concatMap,
  filter,
  map,
  reduce,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { PlatformService } from '../_services/platform.service';
import { ErrorDialog } from './_components/error-dialog/error-dialog';
import { StorageService } from './storage.service';

export interface InitialiseFiuuSeamlessPaymentPayload {
  productId: number;
  amount: number;
  channel: string;
  appName: string;
  countryId: number;
}

export interface InitialiseFiuuSeamlessPaymentResponse {
  success: boolean;
  message: string;
  data: {
    merchantId: string;
    orderId: string;
    amount: string;
    currency: string;
    channel: string;
    vcode: string;
    returnUrl: string;
    callbackUrl: string;
    customerEmail: string;
    customerName: string;
  };
}

export interface PaymentMethod {
  imagePath?: string;
  matIcon: string;
  displayName: string;
  paymentType: string;
  takeAction?: (paymentMethod: PaymentMethod) => void;
}

@Injectable({
  providedIn: 'root',
})
export class AppService {
  readonly appCountryName: string = 'Malaysia';
  readonly appName: string = 'laundryMart';
  private redirectUrl: string | null = null;

  appUuid: string = null;
  pushNotificationToken: string = null;

  accessToken: string = null;
  refreshToken: string = null;
  userId: number = null;

  _appCountry$: BehaviorSubject<any> = new BehaviorSubject(null);
  appCountry$ = this._appCountry$.pipe(filter((v) => v !== null));
  languages: BehaviorSubject<any> = new BehaviorSubject([]);
  selectedLanguage: any = null;
  news: BehaviorSubject<any> = new BehaviorSubject([]);
  walletBalance: BehaviorSubject<any> = new BehaviorSubject(null);
  locationsStream: BehaviorSubject<any> = new BehaviorSubject([]);
  rewardPointsStream: BehaviorSubject<any> = new BehaviorSubject(null);

  startMachineTimeoutSeconds: number = 300;

  readonly isLoggedIn$ = new BehaviorSubject<boolean>(false);

  constructor(
    private router: Router,
    private domSanitizer: DomSanitizer,
    private httpClient: HttpClient,
    private loadingController: LoadingController,
    private platform: Platform,
    private toastController: ToastController,
    private matDialog: MatDialog,
    private screenshot: Screenshot,
    private platformService: PlatformService,
    private storageService: StorageService,
  ) {}

  async init(): Promise<void> {
    try {
      await this.storageService.initStorage();

      this.accessToken = await this.storageService.get('accessToken');
      this.refreshToken = await this.storageService.get('refreshToken');
      this.selectedLanguage = await this.storageService.get('selectedLanguage');
      this.userId = await this.storageService.get('userId');

      this.isLoggedIn$.next(!!this.accessToken);

      this.appUuid = await this.getAppUuid();

      await this.refreshAppCountry().toPromise();

      this.registerDeviceForNotifications();
    } catch (error) {
      console.log('init appservice error', error);
    }
  }

  setRedirectUrl(url: string) {
    this.redirectUrl = url;
  }

  getRedirectUrl(): string | null {
    return this.redirectUrl;
  }

  clearRedirectUrl() {
    this.redirectUrl = null;
  }

  async isLoggedIn(): Promise<boolean> {
    const token = await this.storageService.get('accessToken');
    // console.error('token:', !!token);
    return !!token; // returns true if token is not null/undefined/empty
  }

  private async getAppUuid(): Promise<string> {
    const id = await Device.getId();
    return id.uuid;
  }

  private setSelectedLanguage(supportedTranslations): Observable<any> {
    // If already has selected a language then make sure it is still available after refresh
    if (this.selectedLanguage !== null) {
      let found = supportedTranslations.find(
        (v) => v.value === this.selectedLanguage,
      );

      // If still found then do nothing
      if (found) {
        return of(null);
      }
    }

    // Check if API has default preference, then use the default
    let defaultLanguage =
      supportedTranslations.find((v) => v.isDefault)?.value ?? null;

    // If no default from API use the first one
    if (!defaultLanguage) {
      defaultLanguage = supportedTranslations[0]?.value;
    }

    // Set the default
    this.selectedLanguage = defaultLanguage;

    // Save the selected language to storage
    return from(
      this.storageService.set('selectedLanguage', this.selectedLanguage),
    );
  }

  switchLanguage(language: string): Observable<any> {
    this.selectedLanguage = language;

    // Save the selected language to storage
    return from(
      this.storageService.set('selectedLanguage', this.selectedLanguage),
    );
  }

  login(username: string): Observable<any> {
    return this.httpClient.post<any>(
      `${environment.baseApiUrl}/auth/mobileAppLogin`,
      {
        username: username,
      },
    );
  }

  register(
    phoneNumber: string,
    password: string,
    profileName: string,
  ): Observable<any> {
    return this.httpClient.post<any>(
      `${environment.baseApiUrl}/onboarding/register`,
      {
        phoneNumber: phoneNumber,
        password: password,
        profileName: profileName,
      },
    );
  }

  forgotPasswordByEmail(email: string): Observable<any> {
    return this.httpClient.post<any>(
      `${environment.baseApiUrl}/auth/forgotPassword`,
      {
        email: email,
      },
    );
  }
  registerDeviceForNotifications() {
    if (!this.appUuid) {
      throw new Error('No app UUID.');
    }

    if (!this.userId) {
      return;
    }

    this.httpClient
      .post<any>(
        `${environment.baseApiUrl}/account/updateAppInstallationInformation`,
        {
          appUuid: this.appUuid,
          platformName: 'web',
          pushToken: '1234!@#$',
          userId: this.userId,
        },
      )
      .subscribe((res) => {
        if (!res.success) {
          this.showErrorDialog(res.message, res.errors);
          return;
        }
        // console.log('device registered...', res);
      });
  }

  updateAppInstallationInfo(): Observable<any> {
    if (!this.appUuid) {
      throw new Error('No app UUID.');
    }

    if (!this.userId) {
      return;
    }

    return from(this.platformService.getPlatform()).pipe(
      switchMap((platform) => {
        return this.httpClient.post<any>(
          `${environment.baseApiUrl}/account/updateAppInstallationInformation`,
          {
            appUuid: this.appUuid,
            platformName: platform,
            pushToken: '1234!@#$',
            userId: this.userId,
          },
        );
      }),
    );
  }

  refreshAppCountry(): Observable<any> {
    return this.httpClient
      .post<any>(`${environment.baseApiUrl}/country/getQuery`, null, {
        params: {
          filter: `countryName||$eq||${this.appCountryName}`,
        },
      })
      .pipe(
        tap((res) => {
          if (!res.success) {
            return;
          }

          this._appCountry$.next(res.data.result[0]);
        }),
      );
  }

  refreshSupportedTranslations(): Observable<any> {
    return this.httpClient
      .get<any>(`${environment.baseApiUrl}/media/getSupportedTranslations`, {
        params: {
          appName: this.appName,
        },
      })
      .pipe(
        switchMap((res) => {
          if (!res.success) {
            return of(res);
          }

          this.languages.next(res.data.supportedTranslations);

          return this.setSelectedLanguage(res.data.supportedTranslations).pipe(
            map(() => res),
          );
        }),
      );
  }

  getTranslations(language: string): Observable<any> {
    return this.httpClient
      .get<any>(`${environment.baseApiUrl}/media/getTranslations`, {
        params: {
          appName: this.appName,
          language: language,
        },
      })
      .pipe(
        map((res) => {
          if (!res.success) {
            return {};
          }

          return res.data.translations;
        }),
      );
  }

  refreshTokens(): Observable<any> {
    return this.httpClient
      .post<any>(`${environment.baseApiUrl}/auth/refreshTokens`, {
        refreshToken: this.refreshToken,
      })
      .pipe(
        switchMap((res) => {
          // console.log('refreshToken response: ', res);
          if (!res.success) {
            return of(res);
          }
          this.accessToken = res.data.accessToken;
          return from(this.storageService.get('accessToken')).pipe(
            switchMap((storedAccessToken) => {
              if (!storedAccessToken) {
                return of(res);
              }
              return from(
                this.storageService.set('accessToken', res.data.accessToken),
              ).pipe(map(() => res));
            }),
          );
        }),
      );
  }

  getProfile(): Observable<any> {
    return this.httpClient.get<any>(`${environment.baseApiUrl}/user/getSelf`);
  }

  updateProfile(profileName?: string, email?: string): Observable<any> {
    console.log('email:', email);
    const dto: any = {
      id: this.userId,
      profileName: profileName,
    };

    if (email != null) {
      if (email.length > 0) {
        dto.email = email;
      }
    }

    return this.httpClient.post<any>(
      `${environment.baseApiUrl}/account/updateProfile`,
      dto,
    );
  }

  getCustomerServiceContactNumber(): Observable<any> {
    return this.httpClient.post<any>(
      `${environment.baseApiUrl}/settings/getQuery`,
      null,
      {
        params: {
          filter: 'type||$eq||Customer Service Number',
        },
      },
    );
  }

  async logout(): Promise<void> {
    this.storageService.clear();
  }

  getTransactionHistory(): Observable<any> {
    return this.httpClient.get<any>(
      `${environment.baseApiUrl}/wallet/getSelfTransactions`,
    );
  }

  refreshWalletBalance(): Observable<any> {
    return this.httpClient
      .get<any>(`${environment.baseApiUrl}/wallet/getSelf`)
      .pipe(
        tap((res) => {
          if (!res.success) {
            return;
          }

          this.walletBalance.next(res.data);
        }),
      );
  }

  getMessages(): Observable<any> {
    return this.httpClient.get<any>(
      `${environment.baseApiUrl}/notification/getSelf`,
    );
  }

  updatePassword(password: string, newPassword: string): Observable<any> {
    return this.httpClient.post<any>(
      `${environment.baseApiUrl}/account/updatePassword`,
      {
        password: password,
        newPassword: newPassword,
      },
    );
  }

  redeemCoupon(couponCode: string): Observable<any> {
    return this.appCountry$.pipe(
      take(1),
      switchMap((appCountry) =>
        this.httpClient.post<any>(
          `${environment.baseApiUrl}/reward/redeemCoupon`,
          {
            countryId: appCountry.id,
            couponCode: couponCode,
          },
        ),
      ),
    );
  }

  refreshNews(): Observable<any> {
    return from(this.platformService.getPlatform()).pipe(
      switchMap((platform) => {
        const platformProper =
          platform.charAt(0).toUpperCase() + platform.slice(1);

        const filterPlatform = platform === 'web' ? 'Android' : platformProper;

        return this.httpClient
          .post<any>(`${environment.baseApiUrl}/news/getQuery`, null, {
            params: {
              filter: [
                'isActive||$eq||1',
                `displayOn${filterPlatform}||$eq||1`,
                `publishedDatetime||$lte||${DateTime.local().toISODate()}`,
              ].join(';'),
              sort: 'publishedDatetime,DESC',
            },
          })
          .pipe(
            switchMap((res) => {
              if (!res.success) {
                return of(res);
              }

              return from<any[]>(res.data.result).pipe(
                concatMap((news) => {
                  if (!news.bannerImage) {
                    return of(news);
                  }

                  return this.getAsset(news.bannerImageUri).pipe(
                    tap((blob) => {
                      const objectUrl = window.URL.createObjectURL(blob);

                      Object.defineProperty(news, 'bannerImagePreview', {
                        configurable: false,
                        enumerable: false,
                        value:
                          this.domSanitizer.bypassSecurityTrustUrl(objectUrl),
                        writable: false,
                      });

                      const url = new URL(news.bannerImageUri);
                      const parts = url.searchParams.get('uri').split('/');
                      Object.defineProperty(news, 'bannerImageFile', {
                        configurable: false,
                        enumerable: false,
                        value: [new File([blob], parts[parts.length - 1])],
                        writable: false,
                      });
                    }),
                    map((v) => news),
                  );
                }),
                concatMap((news) => {
                  if (!news.bannerIcon) {
                    return of(news);
                  }

                  return this.getAsset(news.bannerIconUri).pipe(
                    tap((blob) => {
                      const objectUrl = window.URL.createObjectURL(blob);

                      Object.defineProperty(news, 'bannerIconPreview', {
                        configurable: false,
                        enumerable: false,
                        value:
                          this.domSanitizer.bypassSecurityTrustUrl(objectUrl),
                        writable: false,
                      });

                      const url = new URL(news.bannerIconUri);
                      const parts = url.searchParams.get('uri').split('/');
                      Object.defineProperty(news, 'bannerIconFile', {
                        configurable: false,
                        enumerable: false,
                        value: [new File([blob], parts[parts.length - 1])],
                        writable: false,
                      });
                    }),
                    map((v) => news),
                  );
                }),
                reduce((acc, v) => {
                  return [...acc, v];
                }, []),
                tap((news) => {
                  this.news.next(news);
                }),
                map(() => res),
              );
            }),
          );
      }),
    );
  }

  async scan(): Promise<string> {
    if (!this.platform.is('capacitor')) {
      throw new Error('Unable to launch scanner. Platform not supported.');
    }

    const permission = await BarcodeScanner.checkPermission({ force: true });

    if (permission.denied) {
      throw new Error(
        'Permission to use camera denied. Please enable it manually in the settings.',
      );
    }

    await BarcodeScanner.hideBackground();

    const result = await BarcodeScanner.startScan();

    if (!result.hasContent) {
      throw new Error('No data scanned.');
    }

    await BarcodeScanner.stopScan();

    await BarcodeScanner.showBackground();

    return result.content;
  }

  async stopScan(): Promise<void> {
    await BarcodeScanner.stopScan();

    await BarcodeScanner.showBackground();
  }

  getMachineByQr(scannedCode: string): Observable<any> {
    return this.httpClient.get<any>(
      `${environment.baseApiUrl}/machine/scanMachineQr`,
      {
        params: {
          qr: scannedCode,
        },
      },
    );
  }

  getMachinesByProfileCountryId(): Observable<any> {
    return this.httpClient.get<any>(
      `${environment.baseApiUrl}/machine/listMachines`,
    );
  }

  updateSocialEmail(
    tempUserId: number,
    email: string,
    profileName: string,
  ): Observable<any> {
    return this.httpClient.post<any>(
      `${environment.baseApiUrl}/onboarding/updateSocialEmail`,
      {
        tempUserId: tempUserId,
        email: email,
        profileName: profileName,
      },
    );
  }

  payAndStart(serialNumber: string, productId: number): Observable<any> {
    return this.appCountry$.pipe(
      take(1),
      switchMap((appCountry) => {
        return this.httpClient.post<any>(
          `${environment.baseApiUrl}/machine/payAndStart`,
          {
            countryId: appCountry.id,
            serialNumber: serialNumber,
            productId: productId,
          },
          {
            headers: new HttpHeaders({
              Version: '2',
            }),
          },
        );
      }),
    );
  }

  getTokenPackages(serialNumber: string): Observable<any> {
    return this.httpClient.get<any>(
      `${environment.baseApiUrl}/pricing/getTokenPackage`,
      {
        params: {
          serialNumber: serialNumber,
        },
      },
    );
  }

  refreshLocationsByProfileCountryId(): Observable<any> {
    return this.httpClient
      .get<any>(`${environment.baseApiUrl}/location/listLocations`)
      .pipe(
        tap((res) => {
          if (!res.success) {
            return;
          }

          this.locationsStream.next(res.data.locations);
        }),
      );
  }

  getTopupPackagesByProfileCountryId(): Observable<any> {
    return this.appCountry$.pipe(
      take(1),
      switchMap((appCountry) => {
        return this.httpClient.get<any>(
          `${environment.baseApiUrl}/pricing/getTopUpPackage`,
          {
            params: {
              countryId: appCountry.id,
            },
          },
        );
      }),
    );
  }

  getTopUpPackagePricingDetail(productId: number): Observable<any> {
    return this.httpClient.post<any>(
      `${environment.baseApiUrl}/products/getQuery`,
      null,
      {
        params: {
          filter: `id||$eq||${productId}`,
        },
      },
    );
  }

  getMpayGatewayChannels(): Observable<any> {
    return this.httpClient.post<any>(
      `${environment.baseApiUrl}/gatewayChannels/getQuery`,
      null,
      {},
    );
  }

  initialiseMpayPayment(productId: number, paymentType: string) {
    return this.getMpayGatewayChannels().pipe(
      switchMap((res) => {
        if (!res.success) {
          return of(res);
        }

        const gatewayChannel = res.data.result.filter((v) =>
          v.code.includes(paymentType),
        );

        if (gatewayChannel) {
          return this.appCountry$.pipe(
            take(1),
            switchMap((appCountry) => {
              return this.httpClient.post<any>(
                `${environment.baseApiUrl}/transaction/initialiseMpayPayment`,
                {
                  productId: productId,
                  paymentType: paymentType,
                  gatewayChannelId: gatewayChannel[0].id,
                  countryId: appCountry.id,
                  utcOffset: DateTime.local().toFormat('ZZ'),
                  appName: this.appName,
                },
              );
            }),
          );
        }
      }),
      catchError((err, caught) => {
        return of(null);
      }),
    );
  }

  initialiseRazerGatewayPayment(
    productId: number,
    amount: number,
    channel: string,
  ) {
    return this.getMpayGatewayChannels().pipe(
      switchMap((res) => {
        if (!res.success) {
          return of(res);
        }
        return this.appCountry$.pipe(
          take(1),
          switchMap((appCountry) => {
            // console.log('getMpayGatewayChannels :', res.data.result);
            const gatewayChannel = res.data.result.filter((v) =>
              v.code.includes(channel),
            );

            if (!gatewayChannel) {
              return of({
                success: false,
                message: 'Gateway channel not found.',
              });
            }

            return this.httpClient.post<any>(
              `${environment.baseApiUrl}/transaction/initialiseRazerGatewayPayment`,
              {
                productId: productId,
                amount: amount,
                channel: channel,
                appName: this.appName,
                countryId: appCountry.id,
              },
            );
          }),
        );
      }),
      catchError((err, caught) => {
        return of(null);
      }),
    );
  }

  initialiseFiuuSeamlessPayment(
    productId: number,
    amount: number,
    channel: string,
  ): Observable<InitialiseFiuuSeamlessPaymentResponse | null> {
    return this.appCountry$.pipe(
      take(1),
      switchMap((appCountry) => {
        const payload: InitialiseFiuuSeamlessPaymentPayload = {
          productId: productId,
          amount: amount,
          channel: channel,
          appName: this.appName,
          countryId: appCountry.id,
        };

        return this.httpClient.post<InitialiseFiuuSeamlessPaymentResponse>(
          `${environment.baseApiUrl}/transaction/initialiseFiuuSeamlessPayment`,
          payload,
        );
      }),
      catchError((err, caught) => {
        return of(null);
      }),
    );
  }

  requestRefund(
    locationId: number,
    locationName: string,
    machineId: number,
    machineSerial: string,
    image: Blob,
    imageFileName: string,
    amount: number,
    userRemark: string,
  ): Observable<any> {
    const formData = new FormData();
    formData.append('locationId', locationId.toString());
    formData.append('locationName', locationName);
    formData.append('machineId', machineId.toString());
    formData.append('machineSerial', machineSerial);
    formData.append('amount', amount?.toString());
    if (image) {
      formData.append('imageFile', image, imageFileName);
    }
    if (userRemark) {
      formData.append('userRemark', userRemark.toString());
    }
    formData.append('status', 'Pending');
    formData.append('userId', this.userId.toString());

    return this.httpClient.post<any>(
      `${environment.baseApiUrl}/refundRequests/createWithFile`,
      formData,
    );
  }

  getAsset(uri: string): Observable<any> {
    return this.httpClient.get<any>(uri, {
      responseType: 'blob' as 'json',
    });
  }

  refreshRewardPointUserTransactionSumByProfile(): Observable<any> {
    return this.httpClient
      .get<any>(`${environment.baseApiUrl}/rewardPoint/getSelf`)
      .pipe(
        tap((res) => {
          if (!res.success) {
            return;
          }

          this.rewardPointsStream.next(res.data.result);
        }),
      );
  }

  getRewardPointProductByType(
    type: 'In-House' | 'Partner' | 'Token',
  ): Observable<any> {
    return this.httpClient.post<any>(
      `${environment.baseApiUrl}/rewardPointProducts/getQuery`,
      null,
      {
        params: {
          filter: `rewardProductType||$eq||${type}`,
        },
      },
    );
  }

  redeemTokenRewardPointProductTransaction(
    rewardPointProductId: number,
  ): Observable<any> {
    return this.httpClient.post<any>(
      `${environment.baseApiUrl}/reward/redeemTokenProduct`,
      {
        rewardPointProductId: rewardPointProductId,
      },
    );
  }

  redeemMemberRewardPointProductTransaction(
    recipientName: string,
    recipientAddress: string,
    recipientContactNumber: string,
    rewardPointProductId: number,
  ): Observable<any> {
    return this.httpClient.post<any>(
      `${environment.baseApiUrl}/reward/redeemMemberProduct`,
      {
        recipientName: recipientName,
        recipientAddress: recipientAddress,
        recipientContactNumber: recipientContactNumber,
        rewardPointProductId: rewardPointProductId,
      },
    );
  }

  redeemPartnerRewardPointProductTransaction(
    rewardPointProductId: number,
  ): Observable<any> {
    return this.httpClient.post<any>(
      `${environment.baseApiUrl}/reward/redeemPartnerProduct`,
      {
        rewardPointProductId: rewardPointProductId,
      },
    );
  }

  showErrorDialog(message: string, errors: any[] = []): void {
    this.matDialog.open(ErrorDialog, {
      data: {
        message: message,
        errors: errors,
      },
      disableClose: true,
      panelClass: 'mat-dialog-container-no-padding',
      width: '250px',
    });
  }

  showToast(message: string): Observable<any> {
    return from(
      this.toastController.create({
        message: message,
        duration: 3500,
        buttons: [
          {
            side: 'end',
            icon: 'close',
            role: 'cancel',
          },
        ],
      }),
    ).pipe(switchMap((toast) => from(toast.present())));
  }

  showLoading(message: string): Observable<any> {
    return from(
      this.loadingController.create({
        backdropDismiss: false,
        message: message,
        showBackdrop: true,
      }),
    ).pipe(
      switchMap((loading) => {
        return from(loading.present()).pipe(map(() => loading));
      }),
    );
  }

  googleLogin(): Observable<any> {
    return from(this.platformService.getPlatform()).pipe(
      switchMap((platform) => {
        return this.appCountry$.pipe(
          take(1),
          switchMap((appCountry) => {
            return from(GoogleAuth.signIn()).pipe(
              switchMap((user) => {
                return this.httpClient.post<any>(
                  `${environment.baseApiUrl}/onboarding/googleLogin`,
                  {
                    platformName: platform,
                    idToken: user.authentication.idToken,
                    countryId: appCountry.id,
                    // [2022-06-10 @ Dino] Added this optional appName for backwards compatibility, default will be lbpay-thai
                    appName: this.appName,
                  },
                );
              }),
              switchMap((res) => {
                if (!res.success) {
                  return of(res);
                }

                if (!res.data.accessToken) {
                  return of(res);
                }

                this.accessToken = res.data.accessToken;
                this.refreshToken = res.data.refreshToken;
                this.userId = res.data.userId;

                return from(
                  this.storageService.set('accessToken', res.data.accessToken),
                ).pipe(
                  switchMap(() => {
                    return from(
                      this.storageService.set(
                        'refreshToken',
                        res.data.refreshToken,
                      ),
                    );
                  }),
                  switchMap(() => {
                    return from(
                      this.storageService.set('userId', res.data.userId),
                    );
                  }),
                  map(() => res),
                );
              }),
            );
          }),
        );
      }),
    );
  }

  googleLogout(): Observable<any> {
    return from(GoogleAuth.signOut());
  }

  facebookLogin(): Observable<any> {
    return this.appCountry$.pipe(
      take(1),
      switchMap((appCountry) => {
        return from(
          FacebookLogin.login({
            permissions: ['email', 'public_profile'],
          }),
        ).pipe(
          switchMap((res) => {
            return this.httpClient.post<any>(
              `${environment.baseApiUrl}/onboarding/facebookLogin`,
              {
                token: res.accessToken.token,
                countryId: appCountry.id,
                // [2022-06-10 @ Dino] Added this optional appName for backwards compatibility, default will be lbpay-thai
                appName: this.appName,
              },
            );
          }),
          switchMap((res) => {
            if (!res.success) {
              return of(res);
            }

            if (!res.data.accessToken) {
              return of(res);
            }

            this.accessToken = res.data.accessToken;
            this.refreshToken = res.data.refreshToken;
            this.userId = res.data.userId;

            return from(
              this.storageService.set('accessToken', res.data.accessToken),
            ).pipe(
              switchMap(() => {
                return from(
                  this.storageService.set(
                    'refreshToken',
                    res.data.refreshToken,
                  ),
                );
              }),
              switchMap(() => {
                return from(this.storageService.set('userId', res.data.userId));
              }),
              map(() => res),
            );
          }),
        );
      }),
    );
  }

  facebookLogout(): Observable<any> {
    return from(FacebookLogin.logout());
  }

  appleLogin(): Observable<any> {
    return this.appCountry$.pipe(
      take(1),
      switchMap((appCountry) => {
        return from(
          SignInWithApple.authorize({
            clientId: null,
            redirectURI: null,
            scopes: 'email name',
          }),
        ).pipe(
          switchMap((res) => {
            return this.httpClient.post<any>(
              `${environment.baseApiUrl}/onboarding/appleLogin`,
              {
                idToken: res.response.identityToken,
                countryId: appCountry.id,
                // [2022-06-10 @ Dino] Added this optional appName for backwards compatibility, default will be lbpay-thai
                appName: this.appName,
              },
            );
          }),
          switchMap((res) => {
            if (!res.success) {
              return of(res);
            }

            if (!res.data.accessToken) {
              return of(res);
            }

            this.accessToken = res.data.accessToken;
            this.refreshToken = res.data.refreshToken;
            this.userId = res.data.userId;

            return from(
              this.storageService.set('accessToken', res.data.accessToken),
            ).pipe(
              switchMap(() => {
                return from(
                  this.storageService.set(
                    'refreshToken',
                    res.data.refreshToken,
                  ),
                );
              }),
              switchMap(() => {
                return from(this.storageService.set('userId', res.data.userId));
              }),
              map(() => res),
            );
          }),
        );
      }),
    );
  }

  openUrl(url: string): Observable<any> {
    return from(
      Browser.open({
        url: url,
        toolbarColor: '#fef200',
      }),
    );
  }

  closeBrowser(): Observable<any> {
    return from(Browser.close());
  }

  takePhoto(): Observable<any> {
    return from(Camera.checkPermissions()).pipe(
      switchMap((permission) => {
        if (permission.camera) {
          return of(permission);
        }

        return from(
          Camera.requestPermissions({
            permissions: ['camera'],
          }),
        );
      }),
      switchMap((permission) => {
        if (!permission.camera) {
          // TODO: User does not want to grant permission
          return of(null);
        }

        return from(
          Camera.getPhoto({
            source: CameraSource.Camera,
            resultType: CameraResultType.Uri,
          }),
        );
      }),
    );
  }

  pickImages(): Observable<any> {
    return from(Camera.checkPermissions()).pipe(
      switchMap((permission) => {
        if (permission.photos) {
          return of(permission);
        }

        return from(
          Camera.requestPermissions({
            permissions: ['photos'],
          }),
        );
      }),
      switchMap((permission) => {
        if (!permission.photos) {
          // TODO: User does not want to grant permission
          return of(null);
        }

        return from(
          Camera.getPhoto({
            source: CameraSource.Photos,
            resultType: CameraResultType.Uri,
          }),
        );
      }),
    );
  }

  getCurrentLocation$(): Observable<GeolocationPosition | null> {
    if (Capacitor.isNativePlatform()) {
      return from(
        Geolocation.getCurrentPosition().catch((err) => {
          console.error('Capacitor geolocation error:', err);
          return null;
        }),
      );
    } else if ('geolocation' in navigator) {
      return new Observable<GeolocationPosition>((observer) => {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            observer.next(position);
            observer.complete();
          },
          (error) => {
            observer.error(error);
          },
        );
      }).pipe(
        // Optional: Catch error and return null instead of throwing
        catchError((err) => {
          console.error('Browser geolocation error:', err);
          return of(null);
        }),
      );
    } else {
      console.warn('Geolocation not available');
      return of(null);
    }
  }

  getCurrentPosition(): Observable<Position | null> {
    return from(Geolocation.checkPermissions()).pipe(
      switchMap((permission) => {
        if (
          permission.location === 'granted' ||
          permission.coarseLocation === 'granted'
        ) {
          return of(permission);
        }

        return from(Geolocation.requestPermissions());
      }),
      switchMap((permission) => {
        if (
          permission.location !== 'granted' &&
          permission.coarseLocation !== 'granted'
        ) {
          return of(null);
        }

        return from(Geolocation.getCurrentPosition());
      }),
    );
  }

  launchMapsApp(
    latitude: number,
    longitude: number,
    name: string,
  ): Observable<any> {
    return from(
      StartNavigation.launchMapsApp({
        latitude: latitude,
        longitude: longitude,
        name: name,
      }),
    );
  }

  saveScreenshot(): Observable<any> {
    return from(this.platformService.getPlatform()).pipe(
      switchMap((platform) => {
        if (
          platform !== 'android' &&
          platform !== 'ios' &&
          platform !== 'harmonyos'
        ) {
          throw new Error('Screenshot not supported on this platform');
        }

        return from(this.screenshot.save('png')).pipe(
          switchMap((res) => {
            if (platform !== 'ios') {
              return of(res);
            }

            return from(Media.getAlbums()).pipe(
              switchMap((albumResult) => {
                const screenshotAlbum = albumResult.albums.find(
                  (v) => v.name === 'Screenshots',
                );

                if (screenshotAlbum) {
                  return of(screenshotAlbum);
                }

                return from(
                  Media.createAlbum({
                    name: 'Screenshots',
                  }),
                );
              }),
              switchMap((album) => {
                return from(
                  Media.savePhoto({
                    path: `file://${res.filePath}`,
                    album: album,
                  }),
                ).pipe(map((photo) => res));
              }),
            );
          }),
        );
      }),
    );
  }

  hideSplashScreen(): Observable<any> {
    return from(this.platformService.getPlatform()).pipe(
      switchMap((platform) => {
        if (platform === 'web') {
          return of(null);
        }

        return from(SplashScreen.hide());
      }),
    );
  }

  deleteAccount(): Observable<any> {
    return this.httpClient.post<any>(
      `${environment.baseApiUrl}/account/deleteAccount`,
      null,
    );
  }
}
